import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from "../../hooks/useSEO";
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Grid,
    Button,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Card,
    CardContent,
    Menu,
    MenuItem,
    useTheme,
    useMediaQuery,
    Drawer,
    IconButton,
    Divider,
    Container,
    Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import integrations from "../../data/templates.json";
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';


const categories = [
    'Voice usecase',
    'WhatsApp usecase',
    'OCR usecase',
    'Sales & Marketing'
];

const TemplatesPage = () => {
    const navigate = useNavigate();

    useSEO({
        title: "Templates - Beanbag AI",
        description: "Template page description",
        canonical: "https://www.beanbag.ai/",
        keywords: "AI, artificial intelligence, automation, business solutions, workflow optimization",

        ogTitle: "Beanbag AI",
        ogDescription: "Low-code Agentic AI Platform to Automate Workflows",
        ogImage: "https://www.beanbag.ai/images/beanbag-social-card.jpg",
        ogUrl: "https://www.beanbag.ai/",
        ogType: "website",

        twitterCard: "summary_large_image",
        twitterSite: "@BeanbagAI",

        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Beanbag AI",
            "url": "https://www.beanbag.ai",
            "logo": "https://www.beanbag.ai/logo192.png",
            "description": "Beanbag AI provides intelligent automation solutions to enhance productivity and streamline workflows."
        }
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedCategory, setSelectedCategory] = useState('AI');
    const [sortAnchorEl, setSortAnchorEl] = useState(null);
    const sortOpen = Boolean(sortAnchorEl);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const handleSortClick = (event) => {
        setSortAnchorEl(event.currentTarget);
    };

    const handleSortClose = () => {
        setSortAnchorEl(null);
    };

    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    // Enhanced integrations with sample data for the redesigned cards
    const enhancedIntegrations = integrations.map(integration => ({
        ...integration,
        description: integration.description || `Integration for ${integration.name} to enhance your workflows and productivity.`,
        popularity: integration.popularity || Math.floor(Math.random() * 5) + 1,
        lastUpdated: integration.lastUpdated || '2023-10-15',
        features: integration.features || ['Data sync', 'Automation', 'API access'],
        pricing: integration.pricing || 'Free / Premium'
    }));

    // Filter by category and search term
    const filteredIntegrations = enhancedIntegrations.filter(item => {
        const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
        const matchesSearch = searchTerm === '' ||
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Handle navigation to template detail page
    const handleTemplateClick = (template) => {
        // Create URL-friendly slug from template name
        const templateSlug = template.name.toLowerCase().replace(/\s+/g, '-');

        // Log the navigation path for debugging
        console.log(`Navigating to: /integrations/${templateSlug}/`);

        // Make sure the path includes the leading slash
        navigate(`/integrations/${templateSlug}`, {
            state: { template },
            // Ensure proper URL encoding
            replace: false
        });
    };

    const renderCategories = () => (
        <Box mb={4}>
            <Typography
                variant="h6"
                fontWeight="bold"
                color="white"
                mb={2}
                sx={{
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
                }}
            >
                Categories
            </Typography>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                >
                    {categories.map((category) => (
                        <FormControlLabel
                            key={category}
                            value={category}
                            control={
                                <Radio
                                    sx={{
                                        color: 'white',
                                        '&.Mui-checked': {
                                            color: 'white',
                                        },
                                        padding: { xs: '4px', sm: '8px' }
                                    }}
                                />
                            }
                            label={category}
                            sx={{
                                margin: { xs: '0px 0', sm: '2px 0' },
                                '& .MuiFormControlLabel-label': {
                                    color: 'white',
                                    fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                                    lineHeight: { xs: 1.3, sm: 1.5 }
                                },
                            }}
                        />
                    ))}
                </RadioGroup>
            </FormControl>
        </Box>
    );



    return (
        <Container maxWidth="lg" sx={{ py: 4, marginTop: { xs: "60px", sm: "60px", md: "80px", lg: "100px" } }}>
            <Box sx={{ mb: { xs: 2, sm: 3, md: 15 }, mt: { xs: 2, sm: 3, md: 15 } }}>
                <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="white"
                    sx={{
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                        textAlign: 'center',
                        mb: { xs: 1.5, sm: 2 },
                        letterSpacing: { xs: '-0.01em', md: '-0.02em' },
                        lineHeight: { xs: 1.2, sm: 1.3 }
                    }}
                >
                    Templates
                </Typography>

                <Typography
                    variant="body1"
                    color="white"
                    sx={{
                        opacity: 0.8,
                        maxWidth: '800px',
                        margin: '0 auto',
                        textAlign: 'center',
                        fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1.05rem', lg: '1.1rem' },
                        px: { xs: 2, sm: 0 },
                        lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 }
                    }}
                >
                    Optimize your workflows with these top templates. Seamlessly get started on your goal.
                </Typography>
            </Box>

            <Typography
                variant="h3"
                fontWeight="bold"
                color="white"
                mb={isExtraSmall ? 2 : 4}
                sx={{
                    fontSize: { xs: '1.35rem', sm: '1.75rem', md: '2.25rem', lg: '2.75rem' },
                    textAlign: { xs: 'center', md: 'left' },
                    lineHeight: 1.2
                }}
            >
                Find your template
            </Typography>
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search for templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                    mb: { xs: 3, sm: 4 },
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        color: "white",
                        '&.Mui-focused fieldset': {
                            borderColor: "#e95f43 !important",
                            borderWidth: '2px'
                        },
                        '&:hover fieldset': {
                            borderColor: "#e95f43"
                        }
                    },
                    '& .MuiInputBase-input': {
                        color: "white",
                        padding: { xs: '10px 12px', sm: '12px 14px', md: '16px 14px' },
                        fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: "#e95f43"
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: "#e95f43 !important"
                    },
                    '&.Mui-focused': {
                        color: "#9e9e9e"
                    },
                    outline: 'none',
                    '& .MuiOutlinedInput-root:focus-visible': {
                        outline: 'none'
                    },
                    '& .MuiInputBase-root': {
                        '&:focus-visible': {
                            outline: 'none'
                        }
                    }
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'white', fontSize: { xs: 18, sm: 20, md: 24 } }} />
                        </InputAdornment>
                    ),
                }}
            />

            {isMobile && (
                <Box mb={2.5} display="flex" justifyContent="center">
                    <Button
                        variant="outlined"
                        startIcon={<FilterListIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                        onClick={toggleDrawer}
                        sx={{
                            borderRadius: 5,
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            py: { xs: 0.75, sm: 1 },
                            px: { xs: 2, sm: 3 },
                            width: '100%',
                            maxWidth: '300px',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }}
                    >
                        Filter Categories
                    </Button>
                </Box>
            )}

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: "15px", sm: "20px", md: "40px", lg: "50px" }
                }}
            >
                {!isMobile && (
                    <Box
                        sx={{
                            width: { sm: '220px', md: '230px', lg: '250px' },
                            flexShrink: 0
                        }}
                    >
                        {renderCategories()}
                    </Box>
                )}

                <Drawer
                    anchor="left"
                    open={drawerOpen}
                    onClose={toggleDrawer}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'rgba(35, 22, 66, 0.95)',
                            color: 'white',
                            padding: { xs: 1.5, sm: 2 },
                            width: '80%',
                            maxWidth: '300px'
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            Categories
                        </Typography>
                        <IconButton onClick={toggleDrawer} sx={{ color: 'white', padding: { xs: '4px', sm: '8px' } }}>
                            <CloseIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                        </IconButton>
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 1.5 }} />
                    {renderCategories()}
                </Drawer>

                <Box sx={{ flexGrow: 1 }}>
                    <Box
                        mb={2.5}
                        display="flex"
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        gap={{ xs: 1.5, sm: 2 }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            color="white"
                            sx={{
                                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.4rem' },
                                lineHeight: 1.3
                            }}
                        >
                            {filteredIntegrations.length} Templates
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={handleSortClick}
                            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                            sx={{
                                borderRadius: 5,
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                minWidth: { xs: '130px', sm: '150px' },
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                py: { xs: 0.75, sm: 1 },
                                px: { xs: 1.5, sm: 2 }
                            }}
                        >
                            Sort: Popularity
                        </Button>
                        <Menu
                            anchorEl={sortAnchorEl}
                            open={sortOpen}
                            onClose={handleSortClose}
                            PaperProps={{
                                sx: {
                                    backgroundColor: 'rgba(35, 22, 66, 0.9)',
                                    color: 'white'
                                }
                            }}
                        >
                            <MenuItem onClick={handleSortClose} sx={{ color: 'white', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>Popularity</MenuItem>
                            <MenuItem onClick={handleSortClose} sx={{ color: 'white', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>Alphabetical (A-Z)</MenuItem>
                            <MenuItem onClick={handleSortClose} sx={{ color: 'white', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>Newest</MenuItem>
                        </Menu>
                    </Box>

                    {/* Changed Grid layout to have only 2 cards per row */}
                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                        {filteredIntegrations.map((integration) => (
                            <Grid item xs={12} sm={6} md={6} lg={6} key={integration.name}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        background: 'rgba(255,255,255,0.03)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        color: 'white',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            borderColor: 'rgba(226, 104, 74, 0.4)',
                                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 30px rgba(226, 104, 74, 0.15)',
                                            cursor: 'pointer',
                                        },
                                        borderRadius: 4,
                                    }}
                                    onClick={() => handleTemplateClick(integration)}
                                >
                                    <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                                        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 3, md: 4 }}>
                                            {/* Logo Section */}
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="center"
                                                sx={{
                                                    minWidth: { xs: '100%', sm: '150px' },
                                                    alignSelf: { xs: 'center', sm: 'flex-start' }
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: { xs: 60, sm: 70, md: 80 },
                                                        height: { xs: 60, sm: 70, md: 80 },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: { xs: 2, sm: 3, md: 4 },
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                        fontSize: { xs: '24px', sm: '28px', md: '32px' },
                                                        mb: { xs: 1, sm: 1.5, md: 2 },
                                                    }}
                                                >
                                                    {integration.logo}
                                                </Box>
                                                <Typography
                                                    variant="h6"
                                                    align="center"
                                                    color="white"
                                                    sx={{
                                                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                                                        fontWeight: 'bold',
                                                        mb: { xs: 0.5, sm: 0.75 },
                                                        lineHeight: 1.3
                                                    }}
                                                >
                                                    {integration.name}
                                                </Typography>
                                                <Chip
                                                    label={integration.category}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'rgba(233, 95, 67, 0.2)',
                                                        color: 'white',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        display: { xs: 'flex', sm: 'flex' }
                                                    }}
                                                />
                                            </Box>

                                            {/* Details Section */}
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="white"
                                                    sx={{
                                                        opacity: 0.9,
                                                        mb: 2,
                                                        fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    {integration.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {filteredIntegrations.length > 0 && (
                        <Box display="flex" justifyContent="center" mt={{ xs: 3, sm: 4 }}>
                            <Button
                                variant="outlined"
                                sx={{
                                    borderRadius: 5,
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white',
                                    py: { xs: 0.75, sm: 1 },
                                    px: { xs: 3, sm: 4 },
                                    fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.95rem' },
                                    '&:hover': {
                                        backgroundColor: 'rgba(233, 95, 67, 0.1)',
                                        borderColor: "#e95f43",
                                    }
                                }}
                            >
                                Load More
                            </Button>
                        </Box>
                    )}

                    {filteredIntegrations.length === 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: { xs: 4, sm: 6 }
                            }}
                        >
                            <Typography
                                variant="h6"
                                color="white"
                                align="center"
                                mb={2}
                                sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                            >
                                No Templates found in this category
                            </Typography>
                            <Button
                                variant="outlined"
                                sx={{
                                    borderRadius: 5,
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white',
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    py: { xs: 0.75, sm: 1 },
                                    px: { xs: 2, sm: 3 },
                                    '&:hover': {
                                        backgroundColor: 'rgba(233, 95, 67, 0.1)',
                                        borderColor: "#e95f43",
                                    }
                                }}
                                onClick={() => setSelectedCategory('All')}
                            >
                                View All Templates
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>
        </Container>
    );
};

export default TemplatesPage;